import React, { useState } from 'react';
import { MessageSquare, Send, Inbox, Users, Phone, Mail, AlertCircle } from 'lucide-react';
import { FormInput, FormSelect, FormTextarea } from './shared/FormComponents';
import { LoadingButton } from './shared/LoadingComponents';
import { BUTTON_STYLES, CARD_STYLES, COMMUNICATION_RECIPIENTS } from '../utils/constants';
import { useToast } from '../../../hooks/useToast';

const Communication = ({ loading }) => {
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const [messageData, setMessageData] = useState({
    recipient: '',
    recipientName: '',
    subject: '',
    message: '',
    priority: 'normal',
    sendSMS: false,
    sendEmail: true
  });

  const [activeTab, setActiveTab] = useState('compose');

  // Mock messages data
  const recentMessages = [
    {
      id: 1,
      sender: 'Dr. Smith',
      subject: 'Patient Update Required',
      preview: 'Please update the patient record for John Doe...',
      time: '10:30 AM',
      isRead: false,
      priority: 'high',
      type: 'received'
    },
    {
      id: 2,
      sender: 'Nurse Johnson',
      subject: 'Appointment Confirmation',
      preview: 'The patient has confirmed their appointment...',
      time: '9:45 AM',
      isRead: true,
      priority: 'normal',
      type: 'received'
    }
  ];

  const handleSendMessage = () => {
    console.log('Sending message:', messageData);
    
    // Validation
    if (!messageData.recipient || !messageData.subject || !messageData.message) {
      showError('Please fill in all required fields (recipient, subject, and message)');
      return;
    }
    
    // Simulate sending message
    showInfo('Sending message...');
    
    setTimeout(() => {
      const methods = [];
      if (messageData.sendEmail) methods.push('email');
      if (messageData.sendSMS) methods.push('SMS');
      
      if (methods.length === 0) {
        showWarning('Message composed but no delivery method selected');
        return;
      }
      
      showSuccess(`Message sent successfully via ${methods.join(' and ')} to ${messageData.recipientName || messageData.recipient}`);
      
      // Reset form
      setMessageData({
        recipient: '',
        recipientName: '',
        subject: '',
        message: '',
        priority: 'normal',
        sendSMS: false,
        sendEmail: true
      });
    }, 1500);
  };

  const ComposeMessage = () => (
    <div className={CARD_STYLES.default}>
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Send size={20} className="mr-2" />
          Compose Message
        </h3>
      </div>
      
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect
            label="Recipient Type"
            value={messageData.recipient}
            onChange={(e) => setMessageData({...messageData, recipient: e.target.value})}
            options={COMMUNICATION_RECIPIENTS}
          />
          
          <FormInput
            label="Recipient Name"
            value={messageData.recipientName}
            onChange={(e) => setMessageData({...messageData, recipientName: e.target.value})}
            placeholder="Enter recipient name..."
          />
        </div>

        <FormInput
          label="Subject"
          value={messageData.subject}
          onChange={(e) => setMessageData({...messageData, subject: e.target.value})}
          placeholder="Message subject..."
        />

        <FormTextarea
          label="Message"
          value={messageData.message}
          onChange={(e) => setMessageData({...messageData, message: e.target.value})}
          placeholder="Type your message here..."
          rows={6}
        />

        <div className="flex items-center space-x-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={messageData.sendEmail}
              onChange={(e) => setMessageData({...messageData, sendEmail: e.target.checked})}
              className="mr-2"
            />
            <Mail size={16} className="mr-1" />
            Send Email
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={messageData.sendSMS}
              onChange={(e) => setMessageData({...messageData, sendSMS: e.target.checked})}
              className="mr-2"
            />
            <Phone size={16} className="mr-1" />
            Send SMS
          </label>
        </div>

        <LoadingButton
          loading={loading}
          onClick={handleSendMessage}
          className={BUTTON_STYLES.primary}
        >
          <Send size={16} className="mr-2" />
          Send Message
        </LoadingButton>
      </div>
    </div>
  );

  const MessagesList = () => (
    <div className={CARD_STYLES.default}>
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Inbox size={20} className="mr-2" />
          Recent Messages
        </h3>
      </div>
      
      <div className="divide-y divide-gray-200">
        {recentMessages.map((message) => (
          <div key={message.id} className="p-4 hover:bg-gray-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className={`font-medium ${message.isRead ? 'text-gray-600' : 'text-gray-900'}`}>
                    {message.sender}
                  </span>
                  {message.priority === 'high' && (
                    <AlertCircle size={16} className="text-red-500" />
                  )}
                  <span className="text-sm text-gray-500">{message.time}</span>
                </div>
                <h4 className={`text-sm ${message.isRead ? 'text-gray-600' : 'font-medium text-gray-900'}`}>
                  {message.subject}
                </h4>
                <p className="text-sm text-gray-500 mt-1">{message.preview}</p>
              </div>
              {!message.isRead && (
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex-1 p-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
          <MessageSquare size={24} className="mr-2" />
          Communication Center
        </h2>
        <p className="text-sm text-gray-600">Send messages and manage communications with patients and staff</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-1">
          <nav className="flex space-x-1">
            <button
              onClick={() => setActiveTab('compose')}
              className={`
                flex items-center px-4 py-2 rounded-md font-medium text-sm transition-colors
                ${activeTab === 'compose'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }
              `}
            >
              <Send size={16} className="mr-2" />
              Compose Message
            </button>
            
            <button
              onClick={() => setActiveTab('messages')}
              className={`
                flex items-center px-4 py-2 rounded-md font-medium text-sm transition-colors
                ${activeTab === 'messages'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }
              `}
            >
              <Inbox size={16} className="mr-2" />
              Messages
              {recentMessages.filter(m => !m.isRead).length > 0 && (
                <span className="ml-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs">
                  {recentMessages.filter(m => !m.isRead).length}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveTab('announcements')}
              className={`
                flex items-center px-4 py-2 rounded-md font-medium text-sm transition-colors
                ${activeTab === 'announcements'
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }
              `}
            >
              <Users size={16} className="mr-2" />
              Announcements
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'compose' && <ComposeMessage />}
      {activeTab === 'messages' && <MessagesList />}
      {activeTab === 'announcements' && (
        <div className={CARD_STYLES.default}>
          <div className="p-12 text-center">
            <Users size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Announcements</h3>
            <p className="text-gray-600">Send announcements to all staff members</p>
            <button className={`${BUTTON_STYLES.primary} mt-4`}>
              Create Announcement
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Communication;
